import type { ReactNode } from 'react';
import { Button, Tag } from '@viquard/doc-ui/components';
import { IcnLink } from '@viquard/doc-ui/icons';
import type { CalendarEvent, CalendarEventLink } from '../types.js';

export interface EventLinksProps {
  readonly event: CalendarEvent;
  readonly onSelectLink?:
    | ((link: CalendarEventLink, event: CalendarEvent) => void)
    | undefined;
  readonly density?: 'comfortable' | 'compact' | undefined;
}

/**
 * A horizontal row of chips for the event's linked source resources
 * (article / survey / thread / host / file / member …).
 *
 * Interactive variant uses doc-ui `Button` (variant=secondary,
 * shape=pill); static variant uses doc-ui `Tag`. No custom button.
 */
export function EventLinks(props: EventLinksProps): ReactNode {
  const { event, onSelectLink, density = 'comfortable' } = props;
  const links = event.links;
  if (links === undefined || links.length === 0) return null;

  const interactive = onSelectLink !== undefined;
  const size = density === 'compact' ? 'sm' : 'md';

  return (
    <div
      className={[
        'vq-cal__event-links',
        density === 'compact' ? 'is-compact' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {links.map((link) => {
        const Icon = link.icon ?? IcnLink;
        if (interactive) {
          return (
            <Button
              key={`${link.kind}:${link.id}`}
              variant="secondary"
              size={size}
              shape="pill"
              iconLeft={<Icon size={11} />}
              onClick={(e) => {
                e.stopPropagation();
                onSelectLink(link, event);
              }}
            >
              {link.label}
            </Button>
          );
        }
        return (
          <Tag
            key={`${link.kind}:${link.id}`}
            tone="neutral"
            size={density === 'compact' ? 'sm' : 'md'}
          >
            <span className="vq-cal__event-link-inner">
              <Icon size={11} />
              <span>{link.label}</span>
            </span>
          </Tag>
        );
      })}
    </div>
  );
}
